import React from 'react'
import * as graph from '@adc/sdk'
import * as blueprint from '@adc/blueprint'
import {
  Button as LinkButton,
  Grid,
  Icon,
  Pulser,
  constants,
  Text,
  useApplicationContext,
} from '@adc/ui-adc'
import type { Coordinates, LiveAuction, Maybe, Venue } from '@adc/sdk/types'
import classnames from 'classnames/bind'
import { formatDate } from '../../utils/date'
import styles from './styles.scss'
const { Row, Column } = Grid
const cx = classnames.bind(styles)

type VenueDetailsProps = {
  elmId: string
  venue: Venue & { __computed__?: { address?: string } }
  isMobile?: boolean
}

const openVenueUrl = (venueUrl: string): string => {
  return venueUrl.startsWith('http') ? venueUrl : 'http://' + venueUrl
}

const openMapsUrl = (
  address?: Maybe<string>,
  location?: Maybe<Coordinates>
): string => {
  if (location) {
    const encodedLocation = encodeURIComponent(
      `${location.lat},${location.lon}`
    )
    return `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`
  }
  if (address) {
    return (
      'https://www.google.com/maps/search/?api=1&query=' +
      address.replace(/\s/g, '+')
    )
  }
  return ''
}

const getDirectionsUrl = (
  address?: Maybe<string>,
  location?: Maybe<Coordinates>
): string => {
  if (location?.lat && location?.lon) {
    const encodedLocation = encodeURIComponent(
      `${location.lat},${location.lon}`
    )
    return `https://www.google.com/maps/dir/?api=1&destination=${encodedLocation}`
  }
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`
  }
  return ''
}

const VenueDetails = ({
  elmId,
  venue,
  isMobile = false,
}: VenueDetailsProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { graphWebsocket, featureFlags } = useApplicationContext()
  const mobileId = isMobile ? '_mobile' : ''

  const venueId = venue?.venue_id
  const hasLiveAuction = Boolean(venue?.live_auction)

  const { data: liveAuctionLocationData } =
    blueprint.bidding.hooks.useLiveAuctionLocation({
      enabled: Boolean(hasLiveAuction && venueId),
      graphWebsocket,
      initialData: {
        liveAuctions: venue?.live_auction
          ? [{ ...venue.live_auction, venue_id: venueId }]
          : [],
      },
    })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const liveAuction: Partial<LiveAuction> | undefined =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    liveAuctionLocationData.mapped?.get(venueId)

  if (!venue) {
    return null
  }

  const isAuctioneerOnsiteEnabled = Boolean(
    featureFlags?.['fe.resi.is_auctioneer_onsite_direction']
  )

  const isLocationUpdated = Boolean(
    liveAuction?.location?.lat && liveAuction?.location?.lon
  )
  const hasAuctionCompleted =
    liveAuction?.status === graph.enums.LiveAuctionStatus.Completed
  const isAuctioneerOnsite = isLocationUpdated && !hasAuctionCompleted
  const computedAddress = venue.__computed__?.address
  const directionsUrl = getDirectionsUrl(computedAddress, liveAuction?.location)

  const showAuctioneerOnsite =
    isAuctioneerOnsiteEnabled && directionsUrl && isAuctioneerOnsite
  const showMapItButton =
    !isAuctioneerOnsiteEnabled && (liveAuction?.location || computedAddress)

  const linkText = [venue.venue_title, computedAddress]
    .filter(Boolean)
    .join(' - ')

  const venueLink = venue.venue_url ? (
    <LinkButton
      link={openVenueUrl(venue.venue_url)}
      className={cx('venue-link-button-override')}
      elmId={`${elmId}_details_url_link${mobileId}`}
      target='_blank'
      role='navigation'
    >
      {linkText}
    </LinkButton>
  ) : (
    linkText
  )

  return (
    <Grid>
      <Row>
        <Column mobile={16} tablet={12} computer={12}>
          <div data-elm-id={`${elmId}_details_when${mobileId}`}>
            <b>When:</b>{' '}
            {formatDate(
              venue.venue_schedule?.bid_start_local_date,
              'dddd MMM DD, YYYY'
            )}
            {venue.venue_schedule?.registration_time && (
              <div>
                Registrations starts at{' '}
                {venue.venue_schedule?.registration_time}
              </div>
            )}
          </div>
          {venueLink && (
            <div
              className={cx('mobile-upper-space', 'link-inline-flex')}
              data-elm-id={`${elmId}_details_where${mobileId}`}
            >
              <b>Where:</b> {venueLink}
            </div>
          )}
        </Column>
        <Column
          mobile={16}
          tablet={4}
          computer={4}
          className={cx('map-button-container')}
        >
          {showAuctioneerOnsite && (
            <div className={cx('directions-wrapper')}>
              <LinkButton
                elmId={`${elmId}_details_directions_btn${mobileId}`}
                role='auxiliary'
                color={constants.ColorNames.Error}
                size='small'
                tag='a'
                target='_blank'
                rel='noreferrer'
                link={directionsUrl}
                className={cx({ 'directions-updated': isAuctioneerOnsite })}
              >
                <Icon
                  name='diamond-turn-right'
                  iconStyle='regular'
                  color={constants.ColorNames.Error}
                />{' '}
                Auctioneer Onsite
              </LinkButton>
              <span className={cx('directions-dot')} />
              <Pulser
                className={cx('directions-pulser')}
                width={20}
                color={constants.ColorNames.Error}
              />
              <Text.Body
                color='gray'
                size='extraSmall'
                data-elm-id={`${elmId}_location_updated_label${mobileId}`}
              >
                Exact Auction Location
              </Text.Body>
            </div>
          )}
          {showMapItButton && (
            <LinkButton
              elmId={`${elmId}_details_map_btn${mobileId}`}
              className={cx('button-is-stretch', 'mobile-upper-space')}
              role='primary'
              tag='a'
              target='_blank'
              link={
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                openMapsUrl(computedAddress, liveAuction?.location)
              }
            >
              Map it
            </LinkButton>
          )}
        </Column>
      </Row>
    </Grid>
  )
}

export default VenueDetails
